import { fmt2 } from "@/lib/sqaafLabels";
import { buildGaugeStyle, getDistrictBandLabel } from "@/utils/sqaafExecutiveDerived";

export function getBlockStatus(mean, criticalPct = 0) {
  const score = Number(mean);
  if (!Number.isFinite(score)) return { label: "Developing", tag: "amber" };
  if (score < 58 || Number(criticalPct) >= 14) return { label: "Critical", tag: "red" };
  if (score < 62.5) return { label: "Needs Focus", tag: "red" };
  if (score < 66) return { label: "Developing", tag: "amber" };
  return { label: "Strong", tag: "green" };
}

export function blockCompletionRate(block, districtCompletion = 0) {
  const rate = block?.completion_rate;
  if (rate != null && Number.isFinite(Number(rate))) {
    return Math.round(Number(rate));
  }
  return Math.round(Number(districtCompletion) || 0);
}

export function sortBlocks(blocks = [], sortKey = "intervention") {
  const rows = [...blocks];
  if (sortKey === "mean") {
    rows.sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  } else if (sortKey === "schools") {
    rows.sort((a, b) => (b.schools ?? 0) - (a.schools ?? 0));
  } else if (sortKey === "critical") {
    rows.sort((a, b) => (b.critical_pct ?? 0) - (a.critical_pct ?? 0));
  } else {
    rows.sort((a, b) => (b.intervention_index ?? b.mean ?? 0) - (a.intervention_index ?? a.mean ?? 0));
  }
  return rows;
}

export function filterBlocksByBand(blocks = [], band = "All") {
  if (!band || band === "All") return blocks;
  return blocks.filter((b) => {
    const status = getBlockStatus(b.mean, b.critical_pct);
    return status.label === band;
  });
}

export function buildRankBarRows(blocks = [], bottomCount = 5) {
  const sorted = [...blocks].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  const bottomSet = new Set(
    [...sorted].sort((a, b) => (a.mean ?? 0) - (b.mean ?? 0)).slice(0, bottomCount).map((b) => b.block)
  );
  return sorted.map((b) => ({
    block: b.block,
    mean: Number(b.mean ?? 0),
    width: Math.max(4, Number(b.mean ?? 0)),
    isLow: bottomSet.has(b.block),
  }));
}

export function heatmapCellClass(score) {
  const v = Number(score);
  if (!score || v <= 0) return "blank";
  if (v >= 72) return "score-cell g4";
  if (v >= 65) return "score-cell g3";
  if (v >= 60) return "score-cell g2";
  return "score-cell g1";
}

export function buildHeatmapRows(blockPerf = {}, limit = 10) {
  const sections = (blockPerf?.heatmap_sections || []).slice(0, limit);
  const meanByBlock = new Map(
    (blockPerf?.block_summary || []).map((b) => [b.block, Number(b.mean ?? 0)])
  );
  const rows = (blockPerf?.heatmap || [])
    .slice()
    .sort((a, b) => (meanByBlock.get(b.block) ?? 0) - (meanByBlock.get(a.block) ?? 0))
    .slice(0, 8);
  return { sections, rows };
}

export function buildBlockInsightCards(topBlocks = [], bottomBlocks = [], overview = {}) {
  const districtMean = fmt2(overview.district_mean ?? 0);
  const topDetail = topBlocks
    .slice(0, 3)
    .map((b) => `${b.block} (${fmt2(b.mean)}%)`)
    .join(", ");
  const bottomDetail = bottomBlocks
    .slice(0, 5)
    .map((b) => `${b.block} (${fmt2(b.mean)}%, ${fmt2(b.critical_pct)}% critical)`)
    .join(", ");
  return [
    {
      tone: "green",
      title: "Replicate Top-Block Practices",
      body: `${topDetail || "Top blocks"} exceed the ${districtMean}% district mean and can mentor weaker blocks on governance and completion.`,
    },
    {
      tone: "red",
      title: "Bottom Blocks Need Targeted Review",
      body: `${bottomDetail || "Bottom blocks"} trail the district mean — prioritize section-wise intervention over generic monitoring.`,
    },
    {
      tone: "purple",
      title: "Use Section Gaps for Action",
      body: `Inter-block gap is ${fmt2(overview.block_gap_display)} points. Blocks with intervention index above ${fmt2(bottomBlocks[0]?.intervention_index ?? 0)} need immediate school visits.`,
    },
  ];
}

function comparisonNote(block, kind, districtMean = 0) {
  const mean = fmt2(block.mean ?? 0);
  const critical = fmt2(block.critical_pct ?? 0);
  const below60 = fmt2(block.pct_below_60 ?? 0);
  const schools = Number(block.schools ?? 0);
  if (kind === "top") {
    const lead = fmt2(Number(block.mean ?? 0) - districtMean);
    return `${mean}% mean (+${lead} vs district) · ${schools} schools · ${critical}% critical`;
  }
  const deficit = fmt2(districtMean - Number(block.mean ?? 0));
  return `${mean}% mean (${deficit} below district) · ${below60}% below 60% · index ${fmt2(block.intervention_index ?? 0)}`;
}

export function buildBlockComparisonRows(blocks = [], districtMean = 0) {
  const sorted = [...blocks].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  const top = sorted.slice(0, 3).map((b, i) => ({
    rank: i + 1,
    block: b.block,
    schools: b.schools,
    mean: b.mean,
    note: comparisonNote(b, "top", districtMean),
    kind: "top",
  }));
  const bottom = sorted
    .slice(-3)
    .reverse()
    .map((b, i) => ({
      rank: sorted.length - 2 + i,
      block: b.block,
      schools: b.schools,
      mean: b.mean,
      note: comparisonNote(b, "low", districtMean),
      kind: "low",
    }));
  return [...top, ...bottom];
}

export function buildBlockActionQueue(bottomBlocks = [], overview = {}) {
  const worst = bottomBlocks[0];
  const second = bottomBlocks[1];
  const districtMean = fmt2(overview.district_mean ?? 0);
  return [
    {
      tone: "red",
      priority: "High",
      priorityClass: "",
      title: worst ? `${worst.block} Corrective Plan` : "Bottom Block Corrective Plan",
      body: worst
        ? `Mean ${fmt2(worst.mean)}% (${fmt2(districtMean)}% district) · ${fmt2(worst.critical_pct)}% critical · ${fmt2(worst.pct_below_60)}% below 60%.`
        : "Assign block officers to validate schools below 60%.",
    },
    {
      tone: "orange",
      priority: "Medium",
      priorityClass: "amber",
      title: "Section Evidence Audit",
      body: second
        ? `Review ${second.block} (index ${fmt2(second.intervention_index)}) and sections with blanks or negative district gaps.`
        : "Audit sections with blanks or negative district gaps before next SQAAF refresh.",
    },
    {
      tone: "green",
      priority: "Planned",
      priorityClass: "blue",
      title: "Benchmark Transfer Sprint",
      body: `Transfer top-block practices to ${bottomBlocks.slice(0, 2).map((b) => b.block).join(" and ") || "bottom"} clusters (${fmt2(overview.block_gap_display)}-point inter-block gap).`,
    },
  ];
}

export function buildBlockSummaryRows(blocks = [], districtCompletion = 0) {
  const sorted = [...blocks].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  return sorted.map((b, i) => {
    const status = getBlockStatus(b.mean, b.critical_pct);
    const completion = blockCompletionRate(b, districtCompletion);
    return {
      rank: i + 1,
      block: b.block,
      schools: b.schools,
      mean: fmt2(b.mean),
      median: fmt2(b.median),
      std: fmt2(b.std),
      criticalPct: `${fmt2(b.critical_pct)}%`,
      completion,
      status: status.label,
      statusTag: status.tag,
      meanValue: Number(b.mean ?? 0),
      isStrong: Number(b.mean) >= 66,
      isWeak: Number(b.mean) < 58,
    };
  });
}

export function buildBlockHeroCopy(overview = {}, blocks = []) {
  const mean = overview?.district_mean ?? 0;
  const gaugeStyle = buildGaugeStyle(mean);
  const band = getDistrictBandLabel(mean);
  const blockCount = blocks.length;
  const riskCount = blocks.filter((b) => {
    const status = getBlockStatus(b.mean, b.critical_pct);
    return status.label === "Critical" || status.label === "Needs Focus";
  }).length;
  const sorted = [...blocks].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];
  const gap = fmt2(overview?.block_gap_display ?? 0);

  return {
    gaugeStyle,
    districtMean: fmt2(mean),
    headline: `Block performance is uneven — ${gap}-point inter-block gap`,
    body: best && worst
      ? `${best.block} leads at ${fmt2(best.mean)}% while ${worst.block} is lowest at ${fmt2(worst.mean)}%. ${fmt2(overview?.critical_pct)}% of district schools are critical and ${fmt2(overview?.intervention_load)}% score below 60%.`
      : `${fmt2(overview?.critical_pct)}% of schools are critical across ${blockCount} blocks.`,
    chips: [
      { label: `${band} District Band`, className: "" },
      { label: `${blockCount} Blocks Reviewed`, className: "blue" },
      { label: `${riskCount} Risk Blocks`, className: "red" },
    ],
    actionNote: worst
      ? `Prioritize ${worst.block} (mean ${fmt2(worst.mean)}%, intervention index ${fmt2(worst.intervention_index)}) and ${riskCount} risk blocks for section-level remedial packs.`
      : `Review ${riskCount} risk blocks against the ${fmt2(mean)}% district mean.`,
  };
}

export function buildBlockHeroMetrics(overview = {}, blocks = []) {
  const sorted = [...blocks].sort((a, b) => (b.mean ?? 0) - (a.mean ?? 0));
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];
  return [
    { label: "Best Block", value: best?.block || "—", sub: `${fmt2(best?.mean)} mean score`, badge: "green" },
    { label: "Lowest Block", value: worst?.block || "—", sub: `${fmt2(worst?.mean)} mean score`, badge: "red" },
    { label: "Inter-Block Gap", value: fmt2(overview?.block_gap_display), sub: "Best minus worst block", badge: "purple" },
    { label: "Schools Reviewed", value: Number(overview?.n_schools || 0).toLocaleString(), sub: "Across selected district", badge: "" },
    { label: "Critical Load", value: `${fmt2(overview?.critical_pct)}%`, sub: "Schools scoring ≤ 40", badge: "red" },
    { label: "Completion Rate", value: `${fmt2(overview?.completion_rate)}%`, sub: "Mean answered percentage", badge: "green" },
  ];
}
